import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const REFERRAL_LINK = "https://vegaskarts.com/ref/VK-001";

const SOCIAL_LINKS = [
  {
    label: "TWITTER",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.632L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
      </svg>
    ),
    color: "#E8E6E1",
    href: `https://twitter.com/intent/tweet?text=I%27m%20joining%20Vegas%20Karts%20%E2%80%94%20America%27s%20first%20street%20legal%20go-kart%20tour.%20Join%20my%20crew%20%F0%9F%8F%8E%EF%B8%8F&url=${encodeURIComponent(REFERRAL_LINK)}`,
  },
  {
    label: "WHATSAPP",
    icon: (
      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    ),
    color: "#25D366",
    href: `https://wa.me/?text=I%27m%20joining%20Vegas%20Karts%20%E2%80%94%20America%27s%20first%20street%20legal%20go-kart%20tour.%20Join%20my%20crew%3A%20${encodeURIComponent(REFERRAL_LINK)}`,
  },
  {
    label: "COPY LINK",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
        <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" strokeLinecap="round" />
        <path d="M10.172 13.828a4 4 0 015.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" strokeLinecap="round" />
      </svg>
    ),
    color: "#FF3800",
    action: "copy",
  },
];

export default function SignalBoostModal({ onClose, broadcasts = 847, drivers = 312 }) {
  const [copied, setCopied] = useState(false);
  const [closing, setClosing] = useState(false);
  const [scanPos, setScanPos] = useState(0);
  const animRef = useRef(null);

  // Scan sweep
  useEffect(() => {
    let pos = -10;
    const sweep = () => {
      pos += 0.4;
      if (pos > 110) pos = -10;
      setScanPos(pos);
      animRef.current = requestAnimationFrame(sweep);
    };
    animRef.current = requestAnimationFrame(sweep);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  function dismiss() {
    setClosing(true);
    setTimeout(() => {
      onClose?.();
    }, 380);
  }

  function handleCopy() {
    const text = REFERRAL_LINK;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    } else {
      // Fallback for non-secure contexts or unsupported browsers
      const el = document.createElement("textarea");
      el.value = text;
      el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
      document.body.appendChild(el);
      el.focus();
      el.select();
      try {
        document.execCommand("copy");
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      } catch (err) {
        console.warn("Copy failed:", err);
      } finally {
        document.body.removeChild(el);
      }
    }
  }

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`vsb-backdrop ${closing ? "vsb-out" : "vsb-in"}`}
        onClick={dismiss}
      />

      {/* Modal */}
      <div
        className={`vsb-modal ${closing ? "vsb-modal-out" : "vsb-modal-in"}`}
        role="dialog"
        aria-modal="true"
        aria-label="Signal Boost — Mission Card"
      >
        {/* Scan sweep */}
        <div className="vsb-scan" style={{ top: `${scanPos}%` }} />

        {/* HUD corners */}
        <span className="vsb-corner vsb-tl" />
        <span className="vsb-corner vsb-tr" />
        <span className="vsb-corner vsb-bl" />
        <span className="vsb-corner vsb-br" />

        {/* Close */}
        <button className="vsb-close" onClick={dismiss} aria-label="Close">✕</button>

        {/* Header */}
        <div className="vsb-header">
          <div className="vsb-header-eyebrow">
            <span className="vsb-pulse-dot" />
            SIGNAL BOOST // MISSION CARD
          </div>
          <h2 className="vsb-title">BROADCAST YOUR MISSION</h2>
          <p className="vsb-subtitle">
            Share your driver card to recruit your crew and earn RECON POINTS before launch.
          </p>
        </div>

        <div className="vsb-divider" />

        {/* Driver Card Preview */}
        <div className="vsb-card-preview">
          <div className="vsb-card-inner">
            {/* Card scan effect */}
            <div className="vsb-card-scan-line" />

            <div className="vsb-card-top">
              <div className="vsb-card-logo">
                <div className="vsb-card-logo-hex">
                  <span>V</span>
                </div>
                <div className="vsb-card-logo-text">
                  <span className="vsb-card-vk-name">VEGAS KARTS</span>
                  <span className="vsb-card-vk-sub">DRIVER COMMAND CENTER</span>
                </div>
              </div>
              <div className="vsb-card-id-tag">VK-001</div>
            </div>

            <div className="vsb-card-divider" />

            <div className="vsb-card-body">
              <div className="vsb-card-callsign-wrap">
                <span className="vsb-card-callsign-label">CALLSIGN</span>
                <span className="vsb-card-callsign">DRIVER</span>
              </div>
              <div className="vsb-card-stats">
                <div className="vsb-card-stat">
                  <span className="vsb-card-stat-val orange">50</span>
                  <span className="vsb-card-stat-lbl">XP</span>
                </div>
                <div className="vsb-card-stat-sep" />
                <div className="vsb-card-stat">
                  <span className="vsb-card-stat-val blue">1</span>
                  <span className="vsb-card-stat-lbl">RECRUIT</span>
                </div>
                <div className="vsb-card-stat-sep" />
                <div className="vsb-card-stat">
                  <span className="vsb-card-stat-val" style={{ color: "#b06ef3" }}>0</span>
                  <span className="vsb-card-stat-lbl">MISSIONS</span>
                </div>
              </div>
            </div>

            <div className="vsb-card-footer">
              <span className="vsb-card-status">
                <span className="vsb-card-status-dot" /> SYSTEM ONLINE
              </span>
              <span className="vsb-card-deploy">DEPLOYING WINTER 2026</span>
            </div>
          </div>
        </div>

        {/* Live stats */}
        <div className="vsb-live-stats">
          <div className="vsb-live-stat">
            <span className="vsb-live-num orange">{(broadcasts ?? 0).toLocaleString()}</span>
            <span className="vsb-live-lbl">ACTIVE BROADCASTS</span>
          </div>
          <div className="vsb-live-sep" />
          <div className="vsb-live-stat">
            <span className="vsb-live-num blue">{(drivers ?? 0).toLocaleString()}</span>
            <span className="vsb-live-lbl">DRIVERS REGISTERED</span>
          </div>
        </div>

        {/* Referral link */}
        <div className="vsb-ref-block">
          <div className="vsb-ref-label">YOUR MISSION LINK</div>
          <div className="vsb-ref-row">
            <div className="vsb-ref-link">{REFERRAL_LINK}</div>
            <button
              className={`vsb-ref-copy ${copied ? "copied" : ""}`}
              onClick={handleCopy}
            >
              {copied ? (
                <>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
                    <path d="M3 8l3.5 3.5L13 4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  COPIED
                </>
              ) : (
                <>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
                    <rect x="4" y="4" width="9" height="9" rx="1" />
                    <path d="M12 4V3a1 1 0 00-1-1H3a1 1 0 00-1 1v8a1 1 0 001 1h1" strokeLinecap="round" />
                  </svg>
                  COPY
                </>
              )}
            </button>
          </div>
        </div>

        {/* Social share buttons */}
        <div className="vsb-share-section">
          <div className="vsb-share-label">SHARE VIA</div>
          <div className="vsb-share-btns">
            {SOCIAL_LINKS.map((s) => (
              s.action === "copy" ? (
                <button
                  key={s.label}
                  className={`vsb-share-btn ${copied ? "vsb-share-btn--copied" : ""}`}
                  style={{ "--share-color": s.color }}
                  onClick={handleCopy}
                >
                  <span className="vsb-share-btn-icon">{s.icon}</span>
                  <span>{copied ? "COPIED!" : s.label}</span>
                </button>
              ) : (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vsb-share-btn"
                  style={{ "--share-color": s.color }}
                >
                  <span className="vsb-share-btn-icon">{s.icon}</span>
                  <span>{s.label}</span>
                </a>
              )
            ))}
          </div>
        </div>

        {/* Footer note */}
        <p className="vsb-footer-note">
          Every recruit earns you RECON POINTS · Leaderboard resets at launch · Winter 2026
        </p>
      </div>
    </>,
    document.body
  );
}